import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import {
  DataTable,
  TableContainer,
  TableToolbar,
  TableToolbarContent,
  TableToolbarSearch,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
  TableSelectRow,
  TableBatchActions,
  TableBatchAction,
  TableToolbarAction,
  TableToolbarMenu,
  Button,
  Modal,
  TextInput,
  Dropdown,
  Pagination,

  Grid,
  Column,
  MultiSelect,
  InlineLoading,
  InlineNotification,
} from "@carbon/react";
import { Add, Delete, Filter } from "@carbon/icons-react";
import { callApi } from "../../../services/apiService";
import { useUser } from "../../../context/UserContext";

const headers = [
  { key: "attributeName", header: "Attribute Name" },
  { key: "attributeSpecs", header: "Attribute Specs" },
  { key: "status", header: "Status" },
  { key: "createdBy", header: "Created By" },
  { key: "createdTime", header: "Created Time" },
];

export default function Attribute() {
  const mountedRef = useRef(true);
  const [rows, setRows] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newAttributeName, setNewAttributeName] = useState("");
  const [newAttributeSpecs, setNewAttributeSpecs] = useState(null);
  const [isEdit, setIsEdit] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [specsOptions, setSpecsOptions] = useState([]);
  const { user } = useUser();

  // Filter states
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    attributeSpecs: []
  });

  // Filter options
  const [filterOptions, setFilterOptions] = useState({
    attributeSpecs: []
  });

  // Loading and error states
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Delete modal state
  const [deleteId, setDeleteId] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Validation states
  const [nameError, setNameError] = useState("");
  const [specError, setSpecError] = useState("");

  // Pagination and Search states
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Fallback userId
  const userId = user?.userId || 1;
  const userName = user?.username || `User-${userId}`;

  // ------------------ Search & Filter Logic ------------------
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm.trim().toLowerCase()), 350);
    return () => clearTimeout(t);
  }, [searchTerm]);

  // Extract unique values for filters
  const extractUniqueValues = useCallback((data) => {
    const uniqueSpecs = Array.from(
      new Set(data.map(item => item.attributeSpecs).filter(Boolean))
    ).map(spec => ({ id: spec, text: spec }));
    
    return {
      attributeSpecs: uniqueSpecs
    };
  }, []);

  // Apply filters
  const applyFilters = useCallback((data) => {
    if (!data || data.length === 0) return data;

    return data.filter(row => {
      // Filter by attribute specs
      if (filters.attributeSpecs.length > 0) {
        const hasMatchingSpec = filters.attributeSpecs.some(filter => 
          filter.text === row.attributeSpecs
        );
        if (!hasMatchingSpec) return false;
      }

      return true;
    });
  }, [filters]);

  // Normalize Attribute Response
  const normalizeAttribute = useCallback((item, specsList) => ({
    id: item.id || item.ATTRIBUTE_ID,
    attributeName: item.atr_name || item.attr_name,
    attributeSpecs: item.atr_spec_name || specsList.find((s) => s.id === (item.atr_spec_id || item.ATTRIBUTE_SPEC_ID))?.text || "",
    attributeSpecsId: item.atr_spec_id || item.ATTRIBUTE_SPEC_ID,
    status: item.status || item.STATUS || "Active",
    createdBy: item.created_user,
    createdTime: item.created_at || item.CREATED_TIMESTAMP,
    updatedTime: item.updated_at || item.UPDATED_TIMESTAMP,
  }), []);

  // Fetch Attribute Specs
  const fetchAttributeSpecs = useCallback(async () => {
    try {
      const response = await callApi("GET", "/attribute_specs/");
      const data = Array.isArray(response?.data) ? response.data : [];
      
      const options = data.map((item) => ({
        id: item.id,
        text: item.atr_spec_name,
        createdBy: item.created_user,
        createdTime: item.created_at,
      }));
      setSpecsOptions(options);
      return options; // Return options for use in fetchAttributes
    } catch (error) {
      console.error("Failed to fetch specs:", error.message);
      return [];
    }
  }, []);

  // Fetch Attributes
  const fetchAttributes = useCallback(async (specsList = []) => {
    if (!mountedRef.current) return;
    
    setLoading(true);
    setError("");
    try {
      const response = await callApi("GET", "/attributes/");
      const data = Array.isArray(response?.data) ? response.data : [];
      
      const formattedRows = data.map(item => normalizeAttribute(item, specsList));
      
      if (!mountedRef.current) return;
      setRows(formattedRows);

      // Extract filter options from data
      const filterOptions = extractUniqueValues(formattedRows);
      setFilterOptions(prev => ({
        ...prev,
        attributeSpecs: filterOptions.attributeSpecs
      }));

    } catch (err) {
      console.error("fetchAttributes error:", err);
      if (!mountedRef.current) return;
      setError("Unable to load attributes. Please try again.");
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [extractUniqueValues, normalizeAttribute]);

  // Initial data fetch
  useEffect(() => {
    mountedRef.current = true;
    
    const fetchData = async () => {
      try {
        // Fetch specs first, then attributes with the specs data
        const specsList = await fetchAttributeSpecs();
        await fetchAttributes(specsList);
      } catch (err) {
        console.error("Initial data fetch error:", err);
      }
    };
    
    fetchData();
    
    return () => {
      mountedRef.current = false;
    };
  }, [fetchAttributeSpecs, fetchAttributes]);

  // Filter rows based on search and filters
  const filteredRows = useMemo(() => {
    let filtered = rows;

    // Apply text search
    if (debouncedSearch) {
      filtered = filtered.filter(row =>
        (row.attributeName?.toLowerCase() || '').includes(debouncedSearch) ||
        (row.attributeSpecs?.toLowerCase() || '').includes(debouncedSearch) ||
        (row.status?.toLowerCase() || '').includes(debouncedSearch) ||
        (row.createdBy?.toLowerCase() || '').includes(debouncedSearch)
      );
    }

    // Apply filters
    filtered = applyFilters(filtered);

    return filtered;
  }, [rows, debouncedSearch, applyFilters]);

  const paginatedRows = useMemo(() =>
    filteredRows.slice((page - 1) * pageSize, page * pageSize),
    [filteredRows, page, pageSize]
  );

  // Handle filter changes
  const handleFilterChange = (filterName, selectedItems) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: selectedItems
    }));
  };

  const clearAllFilters = () => {
    setFilters({
      attributeSpecs: []
    });
  };

  // Count active filters
  const activeFilterCount = Object.values(filters).reduce((count, filter) => {
    if (Array.isArray(filter)) {
      return count + filter.length;
    } else if (filter && filter !== "") {
      return count + 1;
    }
    return count;
  }, 0);

  // Reset modal
  const resetModal = () => {
    setIsModalOpen(false);
    setIsEdit(false);
    setEditingRow(null);
    setNewAttributeName("");
    setNewAttributeSpecs(null);
    setNameError("");
    setSpecError("");
  };

  // Add new
  const handleAddNew = () => {
    resetModal();
    setIsModalOpen(true);
  };

  // Edit
  const handleEdit = (row) => {
    const selected = rows.find((r) => r.id === row.id);
    setIsEdit(true);
    setEditingRow(selected);
    setNewAttributeName(selected.attributeName);
    setNewAttributeSpecs({
      id: selected.attributeSpecsId,
      text: selected.attributeSpecs,
    });
    setIsModalOpen(true);
  };

  // Save (Add or Update)
  const handleSave = async (e) => {
    e.preventDefault();
    
    let valid = true;

    if (!newAttributeName.trim()) {
      setNameError("Attribute Name is required");
      valid = false;
    } else {
      setNameError("");
    }

    if (!newAttributeSpecs) {
      setSpecError("Attribute Specs is required");
      valid = false;
    } else {
      setSpecError("");
    }

    if (!valid) return;

    try {
      let payload;

      if (isEdit && editingRow) {
        payload = {
          ATTRIBUTE_ID: editingRow.id,
          ATTRIBUTE_NAME: newAttributeName,
          ATTRIBUTE_SPEC_ID: newAttributeSpecs.id,
          UPDATED_BY_USER_ID: userId,
        };

        await callApi("PUT", "/attributes/", payload);
        setSuccess("Attribute updated successfully.");
      } else {
        payload = {
          ATTRIBUTE_NAME: newAttributeName,
          ATTRIBUTE_SPEC_ID: newAttributeSpecs.id,
          CREATED_BY_USER_ID: userId,
          CREATED_BY_USERNAME: userName,
        };

        await callApi("POST", "/attributes/", payload);
        setSuccess("Attribute added successfully.");
      }

      // Don't set loading here - let the fetchAttributes handle it
      await fetchAttributes(specsOptions);
      resetModal();
    } catch (error) {
      console.error("Failed to save:", error.message);
      setError("Failed to save attribute. Please try again.");
    }
  };

  // Open delete modal
  const confirmDelete = (id) => {
    setDeleteId(id);
    setIsDeleteModalOpen(true);
  };

  // Delete attribute
  const handleConfirmDelete = async () => {
    setDeleting(true);
    setError("");
    try {
      await callApi("DELETE", `/attributes/${deleteId}`);
      await fetchAttributes(specsOptions);
      setSuccess("Attribute deleted successfully.");
    } catch (error) {
      console.error("Failed to delete attribute:", error.message);
      setError("Failed to delete attribute. Please try again.");
    } finally {
      setDeleting(false);
      setIsDeleteModalOpen(false);
      setDeleteId(null);
    }
  };

  // Batch delete
  const handleBatchDelete = async (selectedRows) => {
    if (!selectedRows || selectedRows.length === 0) return;
    
    setDeleting(true);
    try {
      const deletePromises = selectedRows.map(row =>
        callApi("DELETE", `/attributes/${row.id}`)
      );
      await Promise.all(deletePromises);
      await fetchAttributes(specsOptions);
      setSuccess(`${selectedRows.length} attribute(s) deleted successfully.`);
    } catch (error) {
      console.error("Failed to delete attributes:", error.message);
      setError("Failed to delete attributes. Please try again.");
    } finally {
      setDeleting(false);
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      return new Date(dateString).toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  return (
    <>
      <DataTable rows={paginatedRows} headers={headers} isSortable>
        {({ rows, headers, getHeaderProps, getRowProps, getSelectionProps, getBatchActionProps, selectedRows }) => {
          const batchActionProps = getBatchActionProps();
          
          return (
            <TableContainer title="Attribute">
              <TableToolbar>
                {selectedRows.length > 0 ? (
                  <TableBatchActions {...batchActionProps}>
                    <TableBatchAction
                      renderIcon={Delete}
                      onClick={() => handleBatchDelete(selectedRows)}
                      iconDescription="Delete selected rows"
                    >
                      Delete ({selectedRows.length})
                    </TableBatchAction>
                  </TableBatchActions>
                ) : (
                  <>
                    <TableToolbarContent>
                      <TableToolbarSearch
                        persistent
                        id="attribute-search"
                        placeholder="Filter table"
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />

                      <Button
                        kind="ghost"
                        renderIcon={Filter}
                        hasIconOnly
                        iconDescription="Toggle filters"
                        onClick={() => setShowFilters((v) => !v)}
                      >
                        {activeFilterCount > 0 && `(${activeFilterCount})`}
                      </Button>

                      <Button renderIcon={Add} onClick={handleAddNew}>
                        Add new
                      </Button>
                    </TableToolbarContent>
                  </>
                )}
              </TableToolbar>

              {/* Filter Section */}
              {showFilters && (
                <div
                  style={{
                    width: "100%",
                    padding: "1rem 1.25rem",
                    background: "#f4f4f4",
                    borderBottom: "1px solid #e0e0e0",
                  }}
                >
                  <Grid narrow>
                    <Column lg={3} md={4} sm={4}>
                      <MultiSelect
                        id="filter-attribute-specs"
                        label="Choose "
                        titleText="Attribute Specs"
                        items={filterOptions.attributeSpecs}
                        itemToString={(item) => item?.text || ""}
                        selectedItems={filters.attributeSpecs}
                        onChange={({ selectedItems }) => handleFilterChange("attributeSpecs", selectedItems)}
                      />
                    </Column>
                    <Column lg={1} md={4} sm={4} style={{ display: "flex", alignItems: "flex-end" }}>
                      <Button
                        kind="secondary"
                        size="sm"
                        onClick={clearAllFilters}
                        style={{ width: "100%" }}
                      >
                        Clear
                      </Button>
                    </Column>
                  </Grid>
                </div>
              )}

              {/* Loading and Error Messages */}
              {loading && (
                <div style={{ padding: "1rem", textAlign: "center" }}>
                  <InlineLoading description="Loading attributes..." />
                </div>
              )}

              {error && !loading && (
                <div style={{ padding: "1rem" }}>
                  <InlineNotification
                    kind="error"
                    title="Error"
                    subtitle={error}
                    lowContrast
                    onClose={() => setError("")}
                  />
                </div>
              )}

              {success && (
                <div style={{ padding: "1rem" }}>
                  <InlineNotification
                    kind="success"
                    title="Success"
                    subtitle={success}
                    lowContrast
                    onClose={() => setSuccess("")}
                  />
                </div>
              )}

              <Table>
                <TableHead>
                  <TableRow>
                    <th />
                    {headers.map((header) => (
                      <TableHeader key={header.key} {...getHeaderProps({ header })}>
                        {header.header}
                      </TableHeader>
                    ))}
                    <TableHeader>Actions</TableHeader>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {!loading && paginatedRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={headers.length + 2} style={{ textAlign: 'center', padding: '2rem' }}>
                        {searchTerm || activeFilterCount > 0 
                          ? 'No results found. Try adjusting your search or filters.' 
                          : 'No attributes found.'}
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedRows.map((row) => (
                      <TableRow key={row.id} {...getRowProps({ row })}>
                        <TableSelectRow {...getSelectionProps({ row })} />
                        <TableCell>{row.attributeName}</TableCell>
                        <TableCell>{row.attributeSpecs || "-"}</TableCell>
                        <TableCell>
                          
                            {row.status}
                        </TableCell>
                        <TableCell>{row.createdBy}</TableCell>
                        <TableCell>{formatDate(row.createdTime)}</TableCell>
                        <TableCell>
                          <TableToolbarMenu>
                            <TableToolbarAction onClick={() => handleEdit(row)}>
                              Edit
                            </TableToolbarAction>
                            <TableToolbarAction onClick={() => confirmDelete(row.id)}>
                              Delete
                            </TableToolbarAction>
                          </TableToolbarMenu>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          );
        }}
      </DataTable>

      {/* Pagination */}
      {filteredRows.length > 0 && !loading && (
        <div style={{ padding: "1rem 1.25rem", display: "flex", justifyContent: "flex-end" }}>
          <Pagination
            totalItems={filteredRows.length}
            page={page}
            pageSize={pageSize}
            pageSizes={[5, 10, 20, 50]}
            onChange={({ page: p, pageSize: ps }) => { 
              setPage(p); 
              setPageSize(ps); 
            }}
          />
        </div>
      )}

      {/* Add/Edit Modal */}
      <Modal
        open={isModalOpen}
        modalHeading={isEdit ? "Edit Attribute" : "Add Attribute"}
        primaryButtonText={isEdit ? "Update" : "Save"}
        secondaryButtonText="Cancel"
        onRequestClose={resetModal}
        onRequestSubmit={handleSave}
      >
        <div style={{ marginBottom: "1rem" }}>
          <TextInput
            id="attribute-name"
            labelText="Attribute Name *"
            value={newAttributeName}
            onChange={(e) => setNewAttributeName(e.target.value)}
            placeholder="Enter attribute name"
            invalid={!!nameError}
            invalidText={nameError}
            required
          />
        </div>
        
        <div style={{ marginBottom: "1rem" }}>
          <Dropdown
            id="attribute-specs"
            titleText="Attribute Specs *"
            label="Select Specs"
            items={specsOptions}
            itemToString={(item) => (item ? item.text : "")}
            selectedItem={newAttributeSpecs}
            onChange={({ selectedItem }) => setNewAttributeSpecs(selectedItem)}
            invalid={!!specError}
            invalidText={specError}
            required
          />
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        open={isDeleteModalOpen}
        modalHeading="Confirm Delete"
        primaryButtonText={deleting ? <InlineLoading description="Deleting..." /> : "Delete"}
        secondaryButtonText="Cancel"
        danger
        onRequestClose={() => {
          if (!deleting) setIsDeleteModalOpen(false);
        }}
        onRequestSubmit={handleConfirmDelete}
        primaryButtonDisabled={deleting}
      >
        <p>Are you sure you want to delete this attribute? This action cannot be undone.</p>
      </Modal>
    </>
  );
}